/**
 * "New puja" and "New temple" (admin docs 10 §3.1, 13 §3.1) — the two create forms the catalogue
 * and the temple directory were missing. Each asks only for what the server requires, then hands
 * the new row to its full editor: the nine puja lists and the temple's editorial copy are written
 * there, on the screens that already validate them, not duplicated here.
 *
 * **The slug is proposed, not imposed.** It follows the English name until the operator types one
 * of their own, because a slug is a public URL and some names transliterate badly.
 *
 * **A new temple is always a draft.** The server says so and the form repeats it; publishing is a
 * separate, deliberate step on the Publishing tab.
 */
import { useState } from 'react';
import { Note } from '../components/primitives';
import { Input, Select } from '../components/forms';
import { ScopeAction, Section } from '../components/kit';
import { ErrorState } from '../components/feedback';
import { useConsole } from '../app/console';
import { errorMessage } from '../lib/api/client';
import { useCategories, useCreatePuja, type PujaDetail } from '../lib/api/hooks/catalogue';
import { useCreateTemple } from '../lib/api/hooks/temples';
import { useCities } from '../lib/api/hooks/geo';
import { coordinateError } from './CityEditors';

/** `Satyanarayan Puja` → `satyanarayan-puja`. ASCII only: a Devanagari name needs a typed slug. */
export function proposeSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

const SLUG = /^[a-z0-9]+(-[a-z0-9]+)*$/;
const MINUTES = /^\d{1,5}$/;

function Buttons({ label, disabled, onSave, onCancel }: { label: string; disabled: boolean; onSave: () => void; onCancel: () => void }) {
  const { scope } = useConsole();
  return (
    <div style={{ display: 'flex', gap: 'var(--sp-sm)' }}>
      <ScopeAction scope={scope} need="catalogue_content" size="sm" disabled={disabled} onClick={onSave}>
        {label}
      </ScopeAction>
      <ScopeAction scope={scope} need="catalogue_content" size="sm" variant="ghost" onClick={onCancel}>
        Cancel
      </ScopeAction>
    </div>
  );
}

export function NewPujaForm({ onDone }: { onDone: (created?: PujaDetail) => void }) {
  const { toast } = useConsole();
  const categories = useCategories();
  const create = useCreatePuja();
  const [category, setCategory] = useState('');
  const [nameEn, setNameEn] = useState('');
  const [nameHi, setNameHi] = useState('');
  const [typedSlug, setTypedSlug] = useState<string | null>(null);
  const [significance, setSignificance] = useState('');
  const [minMinutes, setMinMinutes] = useState('60');
  const [maxMinutes, setMaxMinutes] = useState('90');
  const slug = typedSlug ?? proposeSlug(nameEn);
  const categoryId = categories.data?.find((c) => c.name_en === category)?.id;
  const slugError = slug === '' || SLUG.test(slug) ? undefined : 'Lower-case letters, digits and hyphens';
  const minError = MINUTES.test(minMinutes) && Number(minMinutes) >= 1 ? undefined : 'Minutes, at least 1';
  const maxError =
    !MINUTES.test(maxMinutes) || Number(maxMinutes) < 1
      ? 'Minutes, at least 1'
      : !minError && Number(maxMinutes) < Number(minMinutes)
        ? 'Not shorter than the minimum'
        : undefined;
  const valid =
    categoryId !== undefined &&
    nameEn.trim() !== '' &&
    nameHi.trim() !== '' &&
    slug !== '' &&
    !slugError &&
    significance.trim() !== '' &&
    !minError &&
    !maxError;

  return (
    <Section title="New puja">
      <Note>
        Only what a puja cannot exist without. Steps, FAQs, deities, modes and tiers are added on its editor, which
        opens next. Describe traditional significance only — never an outcome.
      </Note>
      <div style={{ display: 'grid', gap: 'var(--sp-sm)', marginTop: 'var(--sp-md)' }}>
        <Select
          label="Category"
          placeholder="Choose a category"
          options={(categories.data ?? []).map((c) => c.name_en)}
          value={category}
          onChange={setCategory}
        />
        <Input label="English name" value={nameEn} onChange={setNameEn} />
        <Input label="Hindi name" value={nameHi} onChange={setNameHi} helper="Devanagari" />
        <Input label="Slug" value={slug} onChange={setTypedSlug} helper={`/puja/${slug || '…'}`} error={slugError} />
        <Input label="Significance" value={significance} onChange={setSignificance} multiline />
        <Input label="Shortest (minutes)" value={minMinutes} onChange={setMinMinutes} inputMode="numeric" error={minError} />
        <Input label="Longest (minutes)" value={maxMinutes} onChange={setMaxMinutes} inputMode="numeric" error={maxError} />
        {create.isError && <ErrorState inline title="The server refused this puja" cause={errorMessage(create.error)} />}
        <Buttons
          label="Create puja"
          disabled={!valid || create.isPending}
          onCancel={() => onDone()}
          onSave={() =>
            create.mutate(
              {
                puja_category_id: categoryId!,
                slug,
                name_en: nameEn.trim(),
                name_hi: nameHi.trim(),
                significance: significance.trim(),
                duration_minutes_min: Number(minMinutes),
                duration_minutes_max: Number(maxMinutes),
              },
              {
                onSuccess: (puja) => {
                  toast({ tone: 'success', message: `${puja.name_en} created` });
                  onDone(puja);
                },
              },
            )
          }
        />
      </div>
    </Section>
  );
}

export function NewTempleForm({ onDone }: { onDone: (createdId?: number) => void }) {
  const { toast } = useConsole();
  const cities = useCities({ page: 1, page_size: 200 });
  const create = useCreateTemple();
  const [cityName, setCityName] = useState('');
  const [name, setName] = useState('');
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  const [address, setAddress] = useState('');
  const cityId = cities.data?.items.find((c) => c.name === cityName)?.id;
  const latError = lat === '' ? undefined : coordinateError(lat, 'lat');
  const lngError = lng === '' ? undefined : coordinateError(lng, 'lng');
  const valid = cityId !== undefined && name.trim() !== '' && lat !== '' && lng !== '' && !latError && !lngError;

  return (
    <Section title="New temple">
      <Note>
        Added as a draft — nothing is public until it is published. Describe it as a temple, never as an official
        partner: that wording needs a signed agreement on the Authorization tab.
      </Note>
      <div style={{ display: 'grid', gap: 'var(--sp-sm)', marginTop: 'var(--sp-md)' }}>
        <Select
          label="City"
          placeholder="Choose a city"
          options={(cities.data?.items ?? []).map((c) => c.name)}
          value={cityName}
          onChange={setCityName}
        />
        <Input label="Temple name" value={name} onChange={setName} helper="The slug is derived from the name and the city" />
        <Input label="Latitude" value={lat} onChange={setLat} inputMode="decimal" error={latError} />
        <Input label="Longitude" value={lng} onChange={setLng} inputMode="decimal" error={lngError} />
        <Input label="Address" value={address} onChange={setAddress} helper="Optional" />
        {create.isError && <ErrorState inline title="The server refused this temple" cause={errorMessage(create.error)} />}
        <Buttons
          label="Create draft"
          disabled={!valid || create.isPending}
          onCancel={() => onDone()}
          onSave={() =>
            create.mutate(
              {
                city_id: cityId!,
                name: name.trim(),
                latitude: lat.trim(),
                longitude: lng.trim(),
                address_line: address.trim() || null,
              },
              {
                onSuccess: (temple) => {
                  toast({ tone: 'success', message: `${temple.name} added as a draft` });
                  onDone(temple.id);
                },
              },
            )
          }
        />
      </div>
    </Section>
  );
}
